import React, { useCallback, useEffect, useState } from "react";
import { logError } from "@/lib/logger";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ProfilePitchModal from "@/components/ProfilePitchModal";
import ApplicationTracker from "@/components/ApplicationTracker";
import WhatsAppOptInCard from "@/components/WhatsAppOptInCard";
import CandidateCVCard from "@/components/CandidateCVCard";
import MyResumeCard from "@/components/MyResumeCard";
import NextActionCard from "@/components/NextActionCard";
import CandidateUnlocksCard from "@/components/CandidateUnlocksCard";
import VerificationUpgradeNudge from "@/components/VerificationUpgradeNudge";
import CandidatePhotoNudge from "@/components/CandidatePhotoNudge";
import {
    Briefcase,
    Sparkle,
    Clock,
    UserPlus,
    Globe,
    Upload,
    User,
    ArrowRight,
    Eye,
    BookmarkSimple,
    Trash,
    Rocket,
    Bell,
} from "@phosphor-icons/react";

const STAGE_STYLE = {
    Applied: "bg-[#F5F5F5] text-[#525252]",
    Screening: "bg-[#EEF2FF] text-[#3730A3]",
    Interview: "bg-[#EFF6FF] text-[#F59E0B]",
    Offer: "bg-[#FAF5FF] text-[#7E22CE]",
    Hired: "bg-[#ECFDF5] text-[#059669]",
    Rejected: "bg-[#FEF2F2] text-[#DC2626]",
};

const SOURCE_META = {
    self_apply: { label: "You applied", icon: User, cls: "text-cobalt" },
    public: { label: "Applied via link", icon: Globe, cls: "text-[#059669]" },
    manual: { label: "Tagged by recruiter", icon: UserPlus, cls: "text-[#7E22CE]" },
    csv: { label: "Imported", icon: Upload, cls: "text-[#A3A3A3]" },
};

function SourcePill({ source }) {
    const meta = SOURCE_META[source] || SOURCE_META.manual;
    const Icon = meta.icon;
    return (
        <span
            data-testid={`source-pill-${source}`}
            className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider ${meta.cls}`}
        >
            <Icon size={10} weight="bold" /> {meta.label}
        </span>
    );
}

export default function CandidateDashboard() {
    const [apps, setApps] = useState([]);
    const [profile, setProfile] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [savedSearches, setSavedSearches] = useState([]);
    const [boost, setBoost] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([
            api.get("/me/applications"),
            api.get("/me/profile"),
            api.get("/me/candidate/analytics").catch(() => ({ data: null })),
            api.get("/me/candidate/saved-searches").catch(() => ({ data: { items: [] } })),
            api.get("/candidate/boost/me").catch(() => ({ data: null })),
        ])
            .then(([a, p, an, ss, bs]) => {
                setApps(a.data);
                setProfile(p.data);
                setAnalytics(an.data);
                setSavedSearches(ss.data?.items || []);
                setBoost(bs.data);
            })
            .catch((e) => logError("Failed to load dashboard:", e))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const active = apps.filter((a) => !["Hired", "Rejected"].includes(a.stage)).length;
    const won = apps.filter((a) => a.stage === "Hired").length;

    return (
        <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-[1100px] mx-auto" data-testid="candidate-dashboard">
            <ProfilePitchModal />
            <div className="mb-8 sm:mb-10">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                    Your hiring journey
                </p>
                <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-ink">
                    My applications.
                </h1>
            </div>

            {/* Continue where you left off — highest-priority CTA of the moment */}
            <NextActionCard />

            {/* V1.8.6 — Add-photo nudge (soft, dismissable for 30 days) */}
            <CandidatePhotoNudge />

            {/* V1.5.8.16 — Tier-aware upgrade nudge for KYC-verified candidates
                (only rendered when tier === "kyc" AND not snoozed in the last 30 days). */}
            <VerificationUpgradeNudge />

            {/* WhatsApp reminder opt-in (Feb 2026) */}
            <WhatsAppOptInCard />

            {/* CV rating history + rate-more CTA (Feb 2026) */}
            <CandidateCVCard />

            {/* Raw CV file (uploaded PDF/DOCX) — V1.5.1 */}
            <MyResumeCard />

            {/* Profile completion banner — Feb 2026: redesigned for high visual
                weight when profile is < 100%. Never uses green (users read
                green as "done"). Amber for < 60%, cobalt for 60-89%, ink for
                90-99%. Explicit action button + missing-field list. */}
            {profile && profile.completion_pct < 100 && (
                <div
                    data-testid="profile-completion-banner"
                    className={`mb-6 border-l-4 ${
                        profile.completion_pct < 60
                            ? "border-l-[#DC2626] bg-rose-50/60"
                            : profile.completion_pct < 90
                              ? "border-l-cobalt bg-cobalt/5"
                              : "border-l-ink bg-[#F5F5F5]"
                    } border-y border-r border-[#E5E5E5] p-4 sm:p-5`}
                >
                    <div className="flex items-start justify-between gap-4 flex-wrap sm:flex-nowrap">
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                                <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${
                                    profile.completion_pct < 60 ? "text-[#DC2626]"
                                        : profile.completion_pct < 90 ? "text-cobalt"
                                        : "text-ink"
                                }`}>
                                    {profile.completion_pct < 60 ? "Action needed"
                                        : profile.completion_pct < 90 ? "Almost there"
                                        : "One step away"}
                                </span>
                                <span
                                    data-testid="profile-completion-pct"
                                    className="text-[10px] font-black tabular-nums text-[#525252]"
                                >
                                    · {profile.completion_pct}% complete
                                </span>
                            </div>
                            <p className="font-heading font-black text-xl sm:text-2xl tracking-tighter text-ink leading-tight">
                                {profile.missing_fields?.length === 1
                                    ? "1 field left to unlock full match scores."
                                    : `${profile.missing_fields?.length || 0} fields left to unlock full match scores.`}
                            </p>
                            {profile.missing_fields?.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="profile-missing-chips">
                                    {profile.missing_fields.map((f) => (
                                        <span
                                            key={f}
                                            className="text-[10px] font-bold uppercase tracking-wider bg-white border border-[#E5E5E5] px-2 py-1 text-[#525252]"
                                        >
                                            {f.replace("_", " ")}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                        <Link
                            to="/candidate/profile"
                            data-testid="profile-completion-cta"
                            className="shrink-0 bg-ink hover:bg-cobalt text-white px-4 sm:px-5 py-2.5 font-black uppercase tracking-wider text-xs transition-colors self-start"
                        >
                            Complete now &rarr;
                        </Link>
                    </div>
                    {/* Slim progress bar at the very bottom of the banner */}
                    <div className="h-1 bg-[#E5E5E5] mt-4 relative overflow-hidden">
                        <div
                            className={`absolute inset-y-0 left-0 transition-all duration-700 ${
                                profile.completion_pct < 60 ? "bg-[#DC2626]"
                                    : profile.completion_pct < 90 ? "bg-cobalt"
                                    : "bg-ink"
                            }`}
                            style={{ width: `${profile.completion_pct}%` }}
                        />
                    </div>
                </div>
            )}

            {/* Boost status / upsell */}
            <BoostBanner boost={boost} />

            {/* Unified upsell tiles — Verified / Pro / Prep / Featured / Bundle */}
            <CandidateUnlocksCard />

            {/* CV View Analytics */}
            {analytics && analytics.total_views > 0 && (
                <div
                    data-testid="cv-views-widget"
                    className="bg-ink text-white p-5 sm:p-6 mb-6 flex items-start gap-4"
                >
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-splash to-cobalt flex items-center justify-center shrink-0">
                        <Eye size={20} weight="bold" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-white/60 mb-1">
                            Your CV, in demand
                        </p>
                        <p className="font-heading text-2xl sm:text-3xl font-black tracking-tighter">
                            Viewed{" "}
                            <span data-testid="cv-total-views" className="text-splash">
                                {analytics.total_views}
                            </span>{" "}
                            {analytics.total_views === 1 ? "time" : "times"}
                        </p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-white/70">
                            {analytics.views_last_7d > 0 && (
                                <span data-testid="cv-views-week">
                                    <span className="font-bold text-white">
                                        {analytics.views_last_7d}
                                    </span>{" "}
                                    this week
                                </span>
                            )}
                            {analytics.last_viewed_at && (
                                <span>
                                    Last viewed {relativeTime(analytics.last_viewed_at)}
                                </span>
                            )}
                        </div>
                        {analytics.per_job?.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-1.5">
                                {analytics.per_job.slice(0, 3).map((row) => (
                                    <span
                                        key={row.job_id}
                                        className="text-[11px] bg-white/10 px-2 py-1 inline-flex items-center gap-1.5"
                                    >
                                        <span className="text-white/60">{row.job_title}</span>
                                        <span className="font-bold text-white">{row.views}</span>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Saved searches */}
            {savedSearches.length > 0 && (
                <div className="mb-6" data-testid="saved-searches-widget">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-2 inline-flex items-center gap-1.5">
                        <BookmarkSimple size={11} weight="bold" /> Saved searches
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {savedSearches.map((s) => {
                            const params = new URLSearchParams();
                            if (s.query) params.set("q", s.query);
                            if (s.location) params.set("location", s.location);
                            if (s.employment_type) params.set("type", s.employment_type);
                            if (s.seniority) params.set("seniority", s.seniority);
                            return (
                                <div
                                    key={s.id}
                                    data-testid={`saved-search-${s.id}`}
                                    className="bg-white border border-[#E5E5E5] hover:border-ink inline-flex items-center gap-1 pl-3 pr-1 py-1 transition-colors"
                                >
                                    <Link
                                        to={`/jobs?${params.toString()}`}
                                        className="text-xs font-bold text-ink truncate max-w-[240px]"
                                    >
                                        {s.name}
                                    </Link>
                                    <button
                                        onClick={async () => {
                                            try {
                                                await api.delete(`/me/candidate/saved-searches/${s.id}`);
                                                setSavedSearches((xs) => xs.filter((x) => x.id !== s.id));
                                            } catch (e) { logError("Delete saved search failed:", e); }
                                        }}
                                        data-testid={`saved-search-delete-${s.id}`}
                                        aria-label="Delete"
                                        className="p-1 text-[#A3A3A3] hover:text-[#DC2626]"
                                    >
                                        <Trash size={11} weight="bold" />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Mini stats */}
            <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-8 sm:mb-10">
                <div className="bg-white border border-[#E5E5E5] p-4 sm:p-6">
                    <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                        Total
                    </p>
                    <p className="font-heading font-black text-3xl sm:text-5xl tracking-tighter text-ink">
                        {apps.length}
                    </p>
                </div>
                <div className="bg-white border border-[#E5E5E5] p-4 sm:p-6">
                    <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                        In progress
                    </p>
                    <p className="font-heading font-black text-3xl sm:text-5xl tracking-tighter text-cobalt">
                        {active}
                    </p>
                </div>
                <div className="bg-white border border-[#E5E5E5] p-4 sm:p-6">
                    <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                        Offers won
                    </p>
                    <p className="font-heading font-black text-3xl sm:text-5xl tracking-tighter text-[#059669]">
                        {won}
                    </p>
                </div>
            </div>

            {loading ? (
                <p className="text-[#525252]">Loading…</p>
            ) : apps.length === 0 ? (
                <div className="bg-white border border-[#E5E5E5] p-10 sm:p-16 text-center">
                    <Briefcase size={36} weight="bold" className="mx-auto text-[#A3A3A3] mb-4" />
                    <p className="font-heading text-2xl font-bold tracking-tight mb-2">
                        No applications yet
                    </p>
                    <p className="text-[#525252] mb-6">
                        Browse open roles and apply with one click.
                    </p>
                    <Link
                        to="/candidate/jobs"
                        data-testid="empty-browse-jobs-link"
                        className="bg-cobalt hover:bg-cobalt-dark text-white px-6 py-3 font-bold tracking-wide text-sm inline-flex items-center gap-2 transition-colors"
                    >
                        Browse roles <ArrowRight size={14} weight="bold" />
                    </Link>
                </div>
            ) : (
                <div className="space-y-4 sm:space-y-5">
                    {apps.map((a) => (
                        <ApplicationTracker key={a.id} application={a} onBoosted={load} />
                    ))}
                </div>
            )}
        </div>
    );
}

function relativeTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const secs = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
    if (secs < 60) return "just now";
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    const days = Math.floor(secs / 86400);    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}



function BoostBanner({ boost }) {
    // Compact banner: shows current boost status if active, otherwise upsell CTA.
    if (!boost) return null;
    if (boost.active) {
        const unlimited = boost.alerts_limit === -1;
        return (
            <Link
                to="/candidate/boost"
                data-testid="dashboard-boost-active"
                className="block bg-ink text-white p-4 sm:p-5 mb-6 hover:bg-black transition-colors"
            >
                <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-splash to-cobalt flex items-center justify-center shrink-0">
                        <Rocket size={18} weight="fill" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-splash mb-1">
                            {boost.plan_name} · Active
                        </p>
                        <p className="font-heading text-base sm:text-lg font-bold">
                            {unlimited ? (
                                <>Unlimited alerts · {boost.days_remaining} days left</>
                            ) : (
                                <>
                                    <span data-testid="dashboard-boost-remaining" className="text-splash">
                                        {boost.alerts_remaining}
                                    </span>{" "}
                                    alerts left · {boost.days_remaining} days
                                </>
                            )}
                        </p>
                    </div>
                    <ArrowRight size={16} weight="bold" className="mt-2 text-white/60" />
                </div>
            </Link>
        );
    }
    return (
        <Link
            to="/candidate/boost"
            data-testid="dashboard-boost-upsell"
            className="block bg-gradient-to-r from-splash/15 via-white to-cobalt/15 border border-cobalt/25 p-4 sm:p-5 mb-6 hover:border-cobalt transition-colors"
        >
            <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-ink text-white flex items-center justify-center shrink-0">
                    <Bell size={18} weight="fill" />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-cobalt mb-1">
                        Boost your search
                    </p>
                    <p className="font-heading text-base sm:text-lg font-bold text-ink">
                        Get email + WhatsApp alerts &amp; recruiter priority
                    </p>
                    <p className="text-[11px] text-[#525252] mt-0.5">
                        Plans from ₹1,000 · Featured in recruiter search
                    </p>
                </div>
                <ArrowRight size={16} weight="bold" className="mt-2 text-cobalt" />
            </div>
        </Link>
    );
}
